import React, { useState, useRef } from 'react';
import { useTasks } from '../hooks/useTasks';
import { useToast } from '../context/ToastContext';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import type { Task } from '../types';

export const Tasks: React.FC = () => {
  const { loading, createTask, updateTask, deleteTask, getTasksByStatus } = useTasks();
  const { showToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignee: '',
    deadline: '',
    priority: 'medium' as Task['priority'],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const taskData = {
        ...formData,
        status: 'todo' as Task['status'],
      };

      if (editingTask) {
        await updateTask(editingTask.id, taskData);
        showToast('Tarefa atualizada com sucesso!', 'success');
      } else {
        await createTask(taskData);
        showToast('Tarefa criada com sucesso!', 'success');
      }

      setIsModalOpen(false);
      resetForm();
    } catch (error: any) {
      showToast(error.message || 'Erro ao salvar tarefa', 'error');
    }
  };

  const handleEdit = (task: Task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      assignee: task.assignee || '',
      deadline: task.deadline || '',
      priority: task.priority,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta tarefa?')) {
      try {
        await deleteTask(id);
        showToast('Tarefa excluída com sucesso!', 'success');
      } catch (error: any) {
        showToast(error.message || 'Erro ao excluir tarefa', 'error');
      }
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      assignee: '',
      deadline: '',
      priority: 'medium',
    });
  };

  const getStatusLabel = (status: Task['status']) => {
    const labels = {
      'todo': 'A Fazer',
      'in-progress': 'Em Progresso',
      'review': 'Em Revisão',
      'done': 'Concluído'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority: Task['priority']) => {
    const labels = {
      'low': 'Baixa',
      'medium': 'Média',
      'high': 'Alta'
    };
    return labels[priority] || priority;
  };

  const getPriorityBadgeClass = (priority: Task['priority']) => {
    const classes = {
      'low': 'badge-info',
      'medium': 'badge-warning',
      'high': 'badge-danger'
    };
    return classes[priority] || 'badge-info';
  };

  const columns: Task['status'][] = ['todo', 'in-progress', 'review', 'done'];

  if (loading) {
    return <div className="text-center py-8">Carregando tarefas...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-medium">Task Board</h2>
        <Button onClick={() => { resetForm(); setIsModalOpen(true); }}>
          + Nova Tarefa
        </Button>
      </div>

      <div className="task-board grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {columns.map((status) => (
          <div key={status} className="kanban-column">
            <div className="column-header">
              <h3 className="font-medium">{getStatusLabel(status)}</h3>
              <span className="badge badge-info">{getTasksByStatus(status).length}</span>
            </div>
            <div className="task-list">
              {getTasksByStatus(status).map((task) => (
                <div key={task.id} className="task-card">
                  <h4 className="font-medium mb-2">{task.title}</h4>
                  {task.description && (
                    <p className="text-sm text-text-secondary mb-3">
                      {task.description.substring(0, 100)}
                      {task.description.length > 100 ? '...' : ''}
                    </p>
                  )}
                  <div className="task-meta flex flex-wrap gap-2 text-xs text-text-muted mb-3">
                    {task.assignee && <span>👤 {task.assignee}</span>}
                    {task.deadline && <span>📅 {new Date(task.deadline).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  <span className={`badge ${getPriorityBadgeClass(task.priority)}`}>
                    {getPriorityLabel(task.priority)}
                  </span>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(task)}>
                      ✏️
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => handleDelete(task.id)}>
                      🗑️
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancelar</Button>
            <Button onClick={() => formRef.current?.requestSubmit()}>{editingTask ? 'Atualizar' : 'Salvar'}</Button>
          </>
        }
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Título</label>
            <input
              className="input w-full"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Descrição</label>
            <textarea
              className="input w-full"
              rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Responsável</label>
            <input
              className="input w-full"
              value={formData.assignee}
              onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prazo</label>
            <input
              type="date"
              className="input w-full"
              value={formData.deadline}
              onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Prioridade</label>
            <select
              className="form-select w-full"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as Task['priority'] })}
            >
              <option value="low">Baixa</option>
              <option value="medium">Média</option>
              <option value="high">Alta</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
};
